import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { createV2BulkTransactions, getCorporateActionSplits, previewCsvTransactions, previewV2BulkTransactions } from '../../composables/fetchAPI';
import { useImportJob } from '../../context/ImportJobContext';
import AIHelpModal from './AIHelpModal'; // ปรับ path ตามโฟลเดอร์จริง
import ReportIssueButton from '../ReportIssueButton'
interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  onSave: () => void;
  subPortId: string;
}

interface CSVPreviewData {
  importedCount: number;
  buyCount: number;
  sellCount: number;
  skippedDuplicateCount: number;
  items: any[];
  errors: string[];
  errorCount: number;     // เพิ่มเข้าไป
  warningCount: number;   // เพิ่มเข้าไป
  autoOpeningCashTransaction?: any; // เพิ่มฟิลด์เงินสดเริ่มต้น
  holdingSummary: any;
  warnings: any[]; // เพิ่มฟิลด์ warnings
  skippedItems: any[]; // เพิ่มฟิลด์ skippedItems
  skipped: any[]; // เพิ่มฟิลด์ skipped
}

const CSVImportModal = ({ isOpen, onClose, portfolioId, subPortId, onSave }: CSVImportModalProps) => {
  const [step, setStep] = useState(1);
  const [broker, setBroker] = useState('');
  const [preview, setPreview] = useState<CSVPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null); // ✅ เพิ่ม State file
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { setJobData } = useImportJob();
  const [editableItems, setEditableItems] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState({ key: 'transactionDate', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState(''); // ✅ เพิ่มบรรทัดนี้
  const [openingCashAmount, setOpeningCashAmount] = useState(0);
  const [isEditing, setIsEditing] = useState(false); // เพิ่ม state นี้
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [sellAllTickers, setSellAllTickers] = useState<Set<string>>(new Set());
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [bulkPreviewData, setBulkPreviewData] = useState<any>(null);
  const [loadingBulkPreview, setLoadingBulkPreview] = useState(false);
  const [apiResult, setApiResult] = useState<any>(null); // เก็บทั้ง data และ error
  const [showConfirmModal, setShowConfirmModal] = useState(false); // แยกออกมาเป็นตัวคุม Modal ยืนยัน
  const handleOpenConfirmPopup = async () => {
    if (!preview) return;

    let transactionsToProcess = [...editableItems];

    sellAllTickers.forEach(ticker => {
      const holding = preview.holdingSummary.openHoldings.find((h: any) => h.ticker === ticker);
      if (!holding) return;

      const sellItems = transactionsToProcess.filter(t => t.assetSymbol === ticker && t.type === 'SELL');
      if (sellItems.length > 0) {
        sellItems.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
        const latestSell = sellItems[0];
        latestSell.quantity = (parseFloat(latestSell.quantity) || 0) + holding.quantity;
        latestSell.price = holding.avgPrice;
      } else {
        const buyItems = transactionsToProcess.filter(t => t.assetSymbol === ticker && t.type === 'BUY');
        buyItems.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
        const sellDate = buyItems.length > 0 ? buyItems[0].transactionDate : new Date().toISOString();

        transactionsToProcess.push({
          transactionDate: sellDate,
          assetSymbol: ticker,
          type: 'SELL',
          quantity: holding.quantity,
          price: holding.avgPrice,
          fee: 0,
          subPortId,
          source: 'USER',
          idempotencyKey: `sell-all-${ticker}-${Date.now()}`
        });
      }
    });

    const filteredTransactions = transactionsToProcess.filter(item => {
      if (item.type === 'SPLIT') return true;
      return item.assetSymbol?.trim() && item.type && (parseFloat(item.quantity) || 0) > 0;
    });

    const finalTransactions = filteredTransactions.map(item => ({
      ...item,
      subPortId,
      fee: item.fee || 0,
      cashCurrency: 'USD',
      source: item.source || 'USER'
    }));

    let itemsToSend = [...finalTransactions];
    if (preview.autoOpeningCashTransaction) {
      const cashFromSellAll = [...sellAllTickers].reduce((total: number, ticker: string) => {
        const holding = preview.holdingSummary.openHoldings.find((h: any) => h.ticker === ticker);
        const qty = parseFloat(holding?.quantity) || 0;
        const price = parseFloat(holding?.avgPrice) || 0;
        return total + (qty * price) + 10;
      }, 0);

      const oldestDateObj = new Date(itemsToSend[0].transactionDate);
      oldestDateObj.setDate(oldestDateObj.getDate() - 1);

      itemsToSend.unshift({
        ...preview.autoOpeningCashTransaction,
        amount: (preview.autoOpeningCashTransaction.amount || 0) + cashFromSellAll,
        transactionDate: oldestDateObj.toISOString()
      });
    }

    setLoadingBulkPreview(true);
    setApiResult(null);
    try {
      const res = await previewV2BulkTransactions(portfolioId, itemsToSend);
      setApiResult(res);
      setShowConfirmPopup(true); // เปิด Popup เพื่อดูผล Preview (รายการหุ้น หรือ Error)
    } catch (e: any) {
      if (e.response?.data) {
        setApiResult(e.response.data);
        setShowConfirmPopup(true);
      } else {
        toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อ');
      }
    } finally {
      setLoadingBulkPreview(false);
    }
  };

  // 2. ฟังก์ชันกดปุ่มขายทั้งหมด
  const toggleSellAll = (ticker: string) => {
    setSellAllTickers(prev => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  };

  const fetchMissingSplits = async (allTransactions: any[], existingHoldings: any[]) => {
    // 1. หา Ticker ใหม่ที่ไม่มีใน openHoldings ของ endpoint
    const existingTickers = new Set(existingHoldings.map(h => h.ticker));
    const newTickers = Array.from(new Set(allTransactions.map(t => t.assetSymbol)))
      .filter(ticker => !existingTickers.has(ticker));

    if (newTickers.length === 0) return [];

    // 2. หาช่วงเวลา
    const dates = allTransactions.map(t => new Date(t.transactionDate).getTime());
    const from = new Date(Math.min(...dates)).toISOString().split('T')[0];
    const to = new Date().toISOString().split('T')[0];

    // 3. ดึงข้อมูล
    const res = await getCorporateActionSplits(newTickers, from, to);
    return res.data; // สมมติว่าคือรายการ SPLIT ที่ได้รับมา
  };

  const handleSellAll = (selectedTickers: string[]) => {
    const newSellTransactions = selectedTickers.map(ticker => {
      const holding = preview?.holdingSummary.openHoldings.find((h: any) => h.ticker === ticker);
      if (!holding) return null;

      return {
        transactionDate: new Date().toISOString(),
        assetSymbol: ticker,
        type: 'SELL',
        quantity: holding.quantity, // ใช้จำนวนที่ถืออยู่ทั้งหมด
        price: 0, // หรือใส่ราคากลางที่ดึงมาได้
        fee: 0,
        subPortId: subPortId,
        idempotencyKey: `sell-all-${ticker}-${Date.now()}`
      };
    }).filter(Boolean);

  };

  // แยก State ระหว่างของเก่า (CSV) และ ของใหม่ (Manual)
  // เมื่อได้ res มา
  useEffect(() => {
    if (preview) setEditableItems(preview.items);
  }, [preview]);
  const sortedAndFilteredData = useMemo(() => {
    let data = [...editableItems];
    if (isEditing) return data;
    if (searchTerm) data = data.filter(item => item.assetSymbol.toLowerCase().includes(searchTerm.toLowerCase()));
    return data.sort((a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime());
  }, [editableItems, searchTerm, isEditing]);

  // ปรับ handleEdit ให้จัดการ Uppercase
  const handleEdit = (key: string, field: string, value: any) => {
    setEditableItems(prev => prev.map(item => {
      if (item.idempotencyKey !== key) return item;
      const newItem = { ...item };
      if (field === 'transactionDate') newItem.transactionDate = new Date(value).toISOString();
      else if (field === 'assetSymbol') newItem.assetSymbol = value.toUpperCase();
      else newItem[field] = value;
      return newItem;
    }));
  };

  const removeRow = (key: string) => {
    setEditableItems(prev => prev.filter(i => i.idempotencyKey !== key));
  };

  // ข้อมูลตัวอย่างสำหรับ Manual Template
  const GOOGLE_SHEET_URL = "https://docs.google.com/spreadsheets/d/1W4AhWF-0N1509GYF3lvtYoVrpagrSBq6aNE7IGKQ5e4/copy"
  const IMPORT_SOURCES = [
    { label: '-- กรุณาเลือกแหล่งที่มา --', broker: '', sourceDataKind: '', importMode: '', policy: '' }, // ตัวเลือกว่าง
    { label: 'Dime transaction history', broker: 'DIME', sourceDataKind: 'RAW_TRANSACTION', importMode: 'RAW_TRANSACTION', policy: 'AUTO_APPLY' },
    { label: 'Webull activity history', broker: 'WEBULL', sourceDataKind: 'RAW_TRANSACTION', importMode: 'RAW_TRANSACTION', policy: 'AUTO_APPLY' },
    // { label: 'M1 tax lots, keep adjusted', broker: 'M1', sourceDataKind: 'ADJUSTED_TAX_LOT', importMode: 'ADJUSTED_TAX_LOT', policy: 'SKIP_HISTORICAL_SPLIT' },
    // { label: 'Webull cost basis report', broker: 'WEBULL', sourceDataKind: 'COST_BASIS_REPORT', importMode: 'ADJUSTED_TAX_LOT', policy: 'SKIP_HISTORICAL_SPLIT' },
    { label: 'M1 tax lots (Reconstruct)', broker: 'M1', sourceDataKind: 'ADJUSTED_TAX_LOT', importMode: 'RECONSTRUCT_RAW_FROM_ADJUSTED_TAX_LOT', policy: 'RECONSTRUCT_AND_APPLY' },
  ];
  const [selectedSource, setSelectedSource] = useState(IMPORT_SOURCES[0]);
  const handlePreview = async () => {
    if (!file) return toast.error("กรุณาเลือกไฟล์ CSV");

    // ✅ เพิ่ม Validation ตรงนี้
    if (!selectedSource.broker) {
      return toast.error("กรุณาเลือกแหล่งที่มา (Source)");
    }
    setLoading(true);
    try {
      const res = await previewCsvTransactions(
        portfolioId,
        file,
        subPortId,
        "PREVIEW_ONLY",
        selectedSource.broker,         // ใช้ค่าจาก Dropdown
        selectedSource.sourceDataKind, // ใช้ค่าจาก Dropdown
        selectedSource.importMode,      // ใช้ค่าจาก Dropdown
        selectedSource.policy           // ใช้ค่าจาก Dropdown
      );

      const previewData = res.data;

      if (previewData.errorCount > 0) {
        toast.error(`พบข้อผิดพลาด ${previewData.errorCount} รายการ`);
      }

      setPreview(previewData);
      setStep(2);
    } catch (e: any) {
      // ✅ ดึง message จาก API ถ้ามี, ถ้าไม่มีให้ใช้ค่าเริ่มต้น
      const errorMessage = e.response?.data?.message || e.message || 'ไม่สามารถตรวจสอบข้อมูลได้';
      toast.error(errorMessage);
      console.error("Preview Error:", e); // เก็บไว้ดูใน Console
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setPreview(null);
  }, [file]);

  const handleCopyPrompt = () => {
    const prompt = "ช่วยแปลงข้อมูล Transaction ในรูปภาพนี้ให้เป็น CSV format โดยใช้หัวข้อ: date, action, ticker, qty, price";
    navigator.clipboard.writeText(prompt);
    toast.success("คัดลอก Prompt แล้ว");
  };

  //   const handleConfirm = async () => {
  //   if (!preview) return;
  //   setLoading(true);
  //   try {
  //     // 1. นำรายการจาก API มาเรียงลำดับใหม่
  //     let transactionsToCreate = [];

  //     // 2. ถ้ามี autoOpeningCashTransaction ให้เอาไว้เป็นลำดับแรกเสมอ [cite: 112]
  //     if (preview.autoOpeningCashTransaction) {
  //       transactionsToCreate.push(preview.autoOpeningCashTransaction);
  //     }

  //     // 3. ตามด้วยรายการซื้อขายทั้งหมดที่ได้รับมา [cite: 58-70]
  //     if (preview.items && Array.isArray(preview.items)) {
  //       transactionsToCreate = [...transactionsToCreate, ...preview.items];
  //     }

  //     // 4. ส่งข้อมูลทั้งหมดแบบ Bulk 
  //     await createV2BulkTransactions(portfolioId, transactionsToCreate);

  //     toast.success('นำเข้าข้อมูลและสร้างรายการเงินสดเริ่มต้นสำเร็จ');
  //     onSave();
  //     onClose();
  //   } catch (e: any) {
  //     toast.error('ยืนยันการนำเข้าไม่สำเร็จ');
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const calculatedOpeningCash = useMemo(() => {
    // รวมทั้งสอง state เพื่อคำนวณ
    const allItems = [...editableItems];
    let minBalance = 0;
    let currentBalance = 0;

    // Sort ตามวันที่ก่อนคำนวณ
    allItems.sort((a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime())
      .forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price) || 0;
        const fee = parseFloat(item.fee) || 0;

        let effect = 0;
        if (item.type === 'BUY') effect = -((qty * price) + fee);
        else if (item.type === 'SELL') effect = (qty * price) - fee;
        else if (item.type === 'DIVIDEND') effect = parseFloat(item.grossAmount) || 0;

        currentBalance += effect;
        if (currentBalance < minBalance) minBalance = currentBalance;
      });
    return Math.ceil(Math.abs(minBalance) + 10);
  }, [editableItems]); // ใส่ newTransactions ลงไปใน Dependency

  // Sync ค่านี้ไปที่ State เงินสด
  // useEffect(() => {
  //   setOpeningCashAmount(calculatedOpeningCash);
  // }, [calculatedOpeningCash]);

  const handleConfirm = async () => {
    if (!preview) return;
    setLoading(true);

    // 1. นำรายการจาก CSV มาจัดการ
    let transactionsToProcess = [...editableItems];

    sellAllTickers.forEach(ticker => {
      const holding = preview.holdingSummary.openHoldings.find((h: any) => h.ticker === ticker);
      if (!holding) return;

      // 2. ค้นหารายการ SELL ล่าสุด "ในรายการที่กำลังจะ Import"
      const sellItems = transactionsToProcess.filter(t => t.assetSymbol === ticker && t.type === 'SELL');

      if (sellItems.length > 0) {
        // ถ้ามีรายการขายใน CSV นี้ ให้แก้ไขรายการนั้นเลย
        sellItems.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());
        const latestSell = sellItems[0];

        // อัปเดตจำนวนและราคา
        latestSell.quantity = (parseFloat(latestSell.quantity) || 0) + holding.quantity;
        latestSell.price = holding.avgPrice; // ใช้ราคาต้นทุนเฉลี่ย
        // ไม่ต้องเปลี่ยน transactionDate ให้เป็นปัจจุบัน ให้คงวันเดิมของรายการขายนั้นไว้!
      } else {
        // 3. ถ้าไม่มีใน CSV ให้เพิ่มรายการใหม่ โดยใช้วันที่ของ "รายการซื้อล่าสุด" ของหุ้นนั้นๆ ใน CSV
        const buyItems = transactionsToProcess.filter(t => t.assetSymbol === ticker && t.type === 'BUY');
        buyItems.sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

        const sellDate = buyItems.length > 0 ? buyItems[0].transactionDate : new Date().toISOString();

        transactionsToProcess.push({
          transactionDate: sellDate, // ใช้วันของรายการซื้อล่าสุด แทนวันปัจจุบัน
          assetSymbol: ticker,
          type: 'SELL',
          quantity: holding.quantity,
          price: holding.avgPrice,
          fee: 0,
          subPortId,
          source: 'USER',
          idempotencyKey: `sell-all-${ticker}-${Date.now()}`
        });
      }
    });

    // 3. กรองข้อมูลขยะ (คงเดิม)
    const filteredTransactions = transactionsToProcess.filter(item => {
      if (item.type === 'SPLIT') return true;
      return item.assetSymbol?.trim() && item.type &&
        (parseFloat(item.quantity) || 0) > 0; // ปรับให้รองรับ quantity
    });

    // 4. จัดการ finalTransactions และ Opening Cash เหมือนเดิม...
    const finalTransactions = filteredTransactions.map(item => ({
      ...item,
      subPortId,
      fee: item.fee || 0,
      cashCurrency: 'USD',
      source: item.source || 'USER'
    }));

    if (preview.autoOpeningCashTransaction) {
      // 1. คำนวณกระแสเงินสดที่ได้รับจากการขายเพิ่มทั้งหมด (Sell All)
      const cashFromSellAll = [...sellAllTickers].reduce((total: number, ticker: string) => {
        const holding = preview.holdingSummary.openHoldings.find((h: any) => h.ticker === ticker);

        // ถ้ารายการขายทั้งหมดนี้สร้างเงินสด (Quantity * Price)
        const qty = parseFloat(holding?.quantity) || 0;
        const price = parseFloat(holding?.avgPrice) || 0;

        return total + (qty * price) + 10;
      }, 0);

      const oldestDateObj = new Date(finalTransactions[0].transactionDate);
      oldestDateObj.setDate(oldestDateObj.getDate() - 1);

      // 2. ปรับปรุง amount ของ Opening Cash โดยนำค่าจาก Backend มาบวกเพิ่มด้วยเงินจากการขายทั้งหมด
      finalTransactions.unshift({
        ...preview.autoOpeningCashTransaction,
        amount: (preview.autoOpeningCashTransaction.amount || 0) + cashFromSellAll,
        transactionDate: oldestDateObj.toISOString()
      });
    }
    try {
      // 5. ส่ง API
      const res = await createV2BulkTransactions(portfolioId, finalTransactions, "ASYNC_SAVE", "BULK_API");
      if (res?.data?.jobId) setJobData({ jobId: res.data.jobId, portId: portfolioId });

      toast.success('นำเข้าสำเร็จ');
      onSave();
      onClose();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ยืนยันไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" >
      <div className="bg-[#18181b] p-6 rounded-xl w-full max-w-2xl border border-gray-700 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mb-6 relative shrink-0">
          <h2 className="text-xl font-bold text-yellow-400">Import CSV รายการซื้อขาย</h2>
          <button onClick={onClose} className="absolute -top-2 -right-2 text-gray-500 hover:text-white transition-colors p-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <p className="text-gray-400 text-xs mt-1">ขั้นตอนที่ {step} จาก 2: {step === 1 ? "เลือกแหล่งข้อมูล" : "อัปโหลดและตรวจสอบรายการ"}</p>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 pb-2 scrollbar-hide">
          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-300 mb-4">เลือกแหล่งข้อมูลที่คุณต้องการนำเข้ารายการซื้อขาย:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {['Manual', 'Webull', 'M1'].map(b => {
                  const isManual = b === 'Manual';
                  return (
                    <button key={b} onClick={() => { if (isManual) { setBroker(b); setStep(2); } }} disabled={!isManual} className={`relative p-4 rounded-lg border transition text-white font-bold overflow-hidden ${isManual ? 'bg-gray-800 border-gray-600 hover:border-yellow-500 cursor-pointer' : 'bg-gray-900 border-gray-700 opacity-60 cursor-not-allowed'}`}>
                      {b}
                      <div className={`text-[10px] font-normal mt-1 ${isManual ? 'text-gray-400' : 'text-gray-500'}`}>{isManual ? 'รองรับทุก Broker' : `นำเข้าผ่านไฟล์ ${b}`}</div>
                      {!isManual && <div className="absolute inset-0 bg-black/20 flex items-center justify-center backdrop-blur-[1px]"><span className="bg-black/90 text-yellow-500 text-[10px] px-2 py-1 rounded border border-yellow-500/30">Coming soon...</span></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {broker === 'Manual' && (
                <>
                  <div className="mt-4">
                    <label className="text-xs text-gray-400 block mb-1">เลือกประเภทไฟล์นำเข้า (Source):<span className="text-red-500 font-bold">*</span></label>
                    <select
                      className={`w-full bg-gray-800 border ${!selectedSource.broker ? 'border-red-500' : 'border-gray-600'} rounded p-2 text-sm text-white`}
                      value={selectedSource.label}
                      disabled={!!preview}
                      onChange={(e) => setSelectedSource(IMPORT_SOURCES.find(s => s.label === e.target.value)!)}
                    >
                      {IMPORT_SOURCES.map(s => (
                        <option key={s.label} value={s.label}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    {!selectedSource.broker && (
                      <p className="text-red-500 text-[10px] mt-1">กรุณาเลือกแหล่งที่มาเพื่อดำเนินการต่อ</p>
                    )}
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg text-sm text-gray-300 mb-4 space-y-2">
                    <p className="font-bold text-yellow-400">คำแนะนำสำหรับโหมด Manual:</p>
                    <div className="flex gap-2">
                      <a href={GOOGLE_SHEET_URL} target="_blank" rel="noopener noreferrer" className="bg-green-600 px-3 py-1 rounded text-white text-xs hover:bg-green-500">ดาวน์โหลด Template</a>
                      <button onClick={() => setIsHelpOpen(true)} className="bg-purple-600 px-3 py-1 rounded text-white text-xs hover:bg-purple-500">ดูคู่มือการใช้ AI ช่วยแปลง</button>
                    </div>
                    <p className="text-xs italic text-gray-400 mt-2">
                      <span className="text-yellow-400">ทางเลือกที่ 1:</span> กด "ดาวน์โหลด Template" เพื่อกรอกข้อมูลใน Google Sheets <br />
                      <span className="text-yellow-400">ทางเลือกที่ 2:</span> กด "คัดลอก Prompt" และทำการ "ส่งรูป Transaction"นั้นๆไปให้ AI ช่วยแปลงเป็นไฟล์ .CSV<br />
                      <span className="text-purple-400">Note: หากเป็นการ broker ของ M1 เหมาะแก่การใช้ ทางเลือกที่ 2(ใช้ AI) ส่วน DIME และ Webull สามารถใช้ได้ทั้ง 2 ทางเลือก </span>
                    </p>
                  </div>
                </>
              )}

              <div className="border-2 border-dashed border-gray-600 p-6 text-center rounded-lg">
                <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)} />
                <p className="text-xs text-gray-500 mt-2">หัวตารางของไฟล์ .csv จำเป็นต้องมี: date, time, action, ticker, qty, price, fee, cost, remark </p>
              </div>

              {preview && (
                <div className="mt-4 space-y-6">

                  {preview?.autoOpeningCashTransaction && (
                    <div className="p-3 bg-green-900/20 border border-green-500 rounded text-[11px] text-green-300 flex justify-between items-center">
                      <p className="font-bold">✅ ตรวจพบการเพิ่มเงินสดเริ่มต้นอัตโนมัติ: จำนวน ${calculatedOpeningCash.toFixed(2)}</p>

                      {/* ✅ ปุ่มกดเพื่อเปิด Warning Popup */}
                      {(preview.warnings?.length > 0 || preview.skippedItems?.length > 0) && (
                        <button
                          onClick={() => setIsWarningModalOpen(true)}
                          className="bg-yellow-400 text-black px-2 py-1 rounded font-bold hover:bg-yellow-500 transition-all text-[10px]"
                        >
                          ⚠️ ดูข้อมูลแจ้งเตือน ({(preview.warnings?.length || 0) + (preview.skippedItems?.length || 0)})
                        </button>
                      )}
                    </div>
                  )}

                  {isWarningModalOpen && (
                    <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
                      <div className="bg-[#18181b] border border-gray-600 p-6 rounded-xl w-full max-w-lg shadow-2xl animate-slideUp">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold text-yellow-400">⚠️ ข้อมูลแจ้งเตือน</h3>
                          <button onClick={() => setIsWarningModalOpen(false)} className="text-gray-400 hover:text-white">✕</button>
                        </div>

                        <div className="max-h-[60vh] overflow-y-auto space-y-4 custom-scrollbar">
                          {/* รายการ Skipped */}
                          {preview?.skippedItems?.length > 0 && (
                            <div className="text-[12px] text-red-300">
                              <p className="font-bold mb-1 underline">รายการที่ถูกตัดออก (Skipped):</p>
                              <ul className="list-disc pl-4 space-y-1">
                                {preview.skippedItems.map((s: any, i: number) => (
                                  <li key={i}>Row {s.rowNo}: {s.ticker || s.value} - {s.thMessage || s.message}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* รายการ Warnings */}
                          {preview?.warnings?.length > 0 && (
                            <div className="text-[12px] text-yellow-300">
                              <p className="font-bold mb-1 underline">คำเตือน (Warnings):</p>
                              <ul className="list-disc pl-4 space-y-1">
                                {preview.warnings.map((w: any, i: number) => (
                                  <li key={i}>{w.message}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        <div className="mt-6 flex justify-end">
                          <button
                            onClick={() => setIsWarningModalOpen(false)}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm"
                          >
                            เข้าใจแล้ว
                          </button>
                        </div>
                      </div>
                    </div>
                  )}


                  {/* ตารางรายการ CSV เดิม */}
                  <div className="space-y-2">
                    {/* <div className="flex gap-2 items-center">
                      <input placeholder="Search Ticker..." className="bg-gray-700 p-2 rounded text-sm flex-1" onChange={(e) => setSearchTerm(e.target.value)} />
                    </div> */}
                    <div className="grid grid-cols-5 gap-2 text-xs bg-gray-800 p-3 rounded text-gray-300">
                      <div>นำเข้า: <span className="text-green-400 font-bold">{preview.importedCount}</span></div>
                      <div>Buy: <span className="text-blue-400">{preview.buyCount}</span></div>
                      <div>Sell: <span className="text-red-400">{preview.sellCount}</span></div>
                      <div>ข้าม: <span className="text-yellow-500">{preview.skippedDuplicateCount}</span></div>
                      <div>แจ้งเตือน: <span className="text-orange-500">{preview.warningCount}</span></div>
                    </div>

                    {/* ❌ เพิ่มส่วนแสดงผล Error จาก API ตรงนี้ */}
                    {preview.errors && preview.errors.length > 0 && (
                      <div className="mt-3 p-3 bg-red-950/40 border border-red-500/50 rounded-lg max-h-40 overflow-y-auto">
                        <p className="text-xs font-bold text-red-400 mb-2 flex items-center gap-1">
                          <span>❌ พบข้อผิดพลาดในไฟล์ CSV ({preview.errors.length} รายการ):</span>
                        </p>
                        <ul className="space-y-1.5 text-[11px] text-red-200">
                          {preview.errors.map((err: any, idx: number) => (
                            <li key={idx} className="flex items-start gap-1.5 bg-red-900/20 p-1.5 rounded border border-red-500/20">
                              <span className="bg-red-600 text-white px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0">
                                แถวที่ {err.rowNo || err.row}
                              </span>
                              <div>
                                <span className="font-semibold text-yellow-300 uppercase">[{err.field}]</span>: {err.thMessage || err.message}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}


                    <div className="overflow-x-auto max-h-60 overflow-y-auto border border-gray-700 rounded">
                      <table className="w-full text-xs text-left text-gray-300">
                        <thead className="bg-gray-800 sticky top-0">
                          <tr>
                            <th className="p-2">วันที่</th>
                            <th className="p-2">Ticker</th>
                            <th className="p-2">ประเภท</th>
                            <th className="p-2">จำนวน</th>
                            <th className="p-2">ราคา</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview?.items.map((item: any, idx: number) => {
                            const isSplit = item.type === 'SPLIT';
                            return (
                              <tr key={idx} className={`border-b ${isSplit ? 'bg-purple-900/10' : 'border-gray-700'}`}>
                                <td className="p-2">{new Date(item.transactionDate).toLocaleDateString()}</td>
                                <td className="p-2 uppercase">{item.assetSymbol}</td>
                                <td className="p-2">{item.type}</td>
                                <td className="p-2">{isSplit ? `Ratio: ${item.metadata?.ratio}` : item.quantity}</td>
                                <td className="p-2">{isSplit ? '-' : item.price?.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Buttons */}
              <div className="shrink-0 flex justify-end gap-3 mt-4 border-t border-gray-700 pt-4">
                {/* ฝั่งซ้าย: ปุ่มแจ้งปัญหา (จำกัดความกว้างไม่ให้ยืด) */}
                <div className="w-fit min-w-[140px]">
                  <ReportIssueButton
                    jobId='...'
                    errorMessage={"แจ้งปัญหาจากหน้า Import CSV"}
                    className="!w-full !py-1.5 md:!py-2 md:!px-8 !rounded-lg"
                  />
                </div>
                <button onClick={() => {
                  setStep(1);
                  setPreview(null); // ✅ พอถอยกลับไป step 1 ก็ล้าง preview เพื่อให้กลับมาแก้ source ได้
                }} className="text-gray-400 text-sm hover:text-white px-4 py-2">ย้อนกลับ</button>
                {!preview && <button onClick={handlePreview} disabled={loading || !file} className={`px-6 py-2 rounded font-bold text-sm ${loading || !file ? 'bg-gray-600' : 'bg-blue-600'}`}>{loading ? 'กำลังประมวลผล...' : 'เริ่มตรวจสอบข้อมูล (Preview)'}</button>}

                {preview && (
                  <>
                    {/* 1. ปุ่มกดสำหรับ Preview ดูผลลัพธ์/เช็ค Error ก่อน */}
                    <button
                      onClick={handleOpenConfirmPopup}
                      disabled={loadingBulkPreview || preview.errorCount > 0}
                      className="px-4 py-2 rounded font-bold text-sm bg-gray-700 hover:bg-gray-600 text-white border border-gray-500"
                    >
                      {loadingBulkPreview ? 'กำลังตรวจสอบ...' : '🔍 พรีวิว'}
                    </button>

                    {/* 2. ปุ่มกดสำหรับยืนยันนำเข้าจริงทันที */}
                    <button
                      onClick={() => setShowConfirmModal(true)}
                      disabled={loading || preview.errorCount > 0}
                      className={`px-6 py-2 rounded font-bold text-sm ${loading || preview.errorCount > 0 ? 'bg-gray-600 text-gray-400' : 'bg-yellow-400 text-black hover:bg-yellow-500'}`}
                    >
                      {loading ? 'กำลังนำเข้า...' : '🚀 ยืนยันการนำเข้า'}
                    </button>
                  </>
                )}
                <AIHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
              </div>
            </div>


          )}
        </div>

{/* Modal ถามยืนยันก่อนบันทึกจริง */}
{showConfirmModal && (
  <div className="fixed inset-0 z-[400] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-[#18181b] border border-yellow-500 p-6 rounded-xl w-full max-w-sm shadow-2xl">
      <h2 className="text-lg font-bold text-yellow-400 mb-2">ยืนยันการนำเข้า?</h2>
      <p className="text-sm text-gray-400 mb-6">คุณแน่ใจหรือไม่ที่จะ Import รายการทั้งหมด {editableItems.length} รายการเข้าสู่พอร์ต? การกระทำนี้ไม่สามารถย้อนกลับได้</p>
      
      <div className="flex gap-3 justify-end">
        <button 
          onClick={() => setShowConfirmModal(false)} 
          className="text-gray-400 px-4 py-2 text-sm hover:text-white"
        >
          ยกเลิก
        </button>
        <button
          onClick={() => {
            setShowConfirmModal(false);
            handleConfirm(); // เรียกฟังก์ชันบันทึกจริงที่นี่
          }}
          className="bg-yellow-400 text-black font-bold px-6 py-2 rounded text-sm hover:bg-yellow-500"
        >
          ยืนยันการบันทึก
        </button>
      </div>
    </div>
  </div>
)}

        {showConfirmPopup && (
          <div className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4">
            <div className="bg-[#18181b] border border-yellow-500 p-6 rounded-xl w-full max-w-lg">
              <h2 className="text-lg font-bold text-yellow-400 mb-4">
                {apiResult?.success ? "📋 ผลการตรวจสอบรายการก่อนนำเข้า" : "⚠️ พบปัญหาในการตรวจสอบข้อมูล"}
              </h2>

              {/* ถ้าพอร์ตมี Error (เช่น หุ้นไม่พอขาย) */}
              {!apiResult?.success && (
                <div className="bg-red-900/20 border border-red-500 p-4 rounded mb-4 text-[12px] text-red-300">
                  <p className="font-bold text-red-400 mb-2">❌ {apiResult?.th_message || "ดำเนินการไม่สำเร็จ"}</p>
                  <div className="bg-red-950/50 p-3 rounded space-y-1">
                    <p className="font-semibold text-yellow-300">
                      {apiResult?.data?.replay?.error?.th_message || apiResult?.data?.replay?.firstAffectedTransaction?.th_reason}
                    </p>
                    {apiResult?.data?.replay?.firstAffectedTransaction && (
                      <div className="mt-2 text-[11px] text-gray-300 border-t border-red-800 pt-2 space-y-0.5">
                        <p>แถวที่เกิดปัญหา (Row): <span className="text-white font-bold">{apiResult.data.replay.firstAffectedTransaction.rowNo}</span></p>
                        <p>สินทรัพย์: <span className="text-white font-bold">{apiResult.data.replay.firstAffectedTransaction.ticker}</span></p>
                        <p>สถานะ: <span className="text-red-400 font-bold">มีหุ้นไม่พอขาย</span></p>
                        <p>ต้องการ: <span className="text-white font-bold">{apiResult.data.replay.firstAffectedTransaction.requiredQuantity}</span>,
                          มีอยู่: <span className="text-white font-bold">{apiResult.data.replay.firstAffectedTransaction.availableQuantity}</span>,
                          ขาดอีก: <span className="text-white font-bold">{apiResult.data.replay.firstAffectedTransaction.shortageQuantity}</span></p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ถ้าตรวจสอบผ่าน (Success) ให้แสดงรายการหุ้นจาก editableItems */}
              {apiResult?.success && (
                <div className="mb-4">
                  <p className="text-sm font-bold text-green-400 mb-2">✅ ผลลัพธ์พอร์ตปลายทางหลังนำเข้า:</p>
                  <div className="max-h-60 overflow-y-auto bg-gray-900 rounded p-2 text-[11px] space-y-2">

                    {/* วนลูป subPorts เพื่อดึง Holdings ออกมาแสดง */}
                    {apiResult.data.portfolioPreview?.subPorts?.map((subPort: any, sIdx: number) => (
                      <div key={sIdx} className="space-y-1">
                        <p className="text-xs font-semibold text-yellow-400 border-b border-gray-700 pb-1">
                          Sub-Portfolio: {subPort.name}
                        </p>

                        {subPort.holdings?.map((holding: any, hIdx: number) => (
                          <div key={hIdx} className="flex justify-between items-center bg-gray-800/60 p-2 rounded text-gray-200">
                            <div>
                              <span className="font-bold text-white text-xs">📈 {holding.symbol}</span>
                              <span className="block text-[10px] text-gray-400">{holding.name}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-green-300">จำนวน: {holding.quantity} หุ้น</p>
                              <p className="text-[10px] text-gray-400">
                                ต้นทุนเฉลี่ย: ${holding.avgEntryPrice?.toFixed(2)} | รวม: ${holding.costValue?.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                    {/* แสดงยอดเงินสดคงเหลือปลายทางด้วย */}
                    <div className="mt-2 pt-2 border-t border-gray-700 flex justify-between items-center text-xs text-gray-300 px-1">
                      <span className="font-bold">💵 เงินสดคงเหลือในพอร์ต:</span>
                      <span className="text-green-400 font-bold">
                        ${apiResult.data.portfolioPreview?.cashBalances?.[0]?.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                      </span>
                    </div>

                  </div>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowConfirmPopup(false)}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded text-sm"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImportModal;